import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConversationProvider } from '@elevenlabs/react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AmbientCanvas from '@/components/humanity/AmbientCanvas';
import ElevenLabsEntityVoice from '@/components/humanity/ElevenLabsEntityVoice';
import SoftButton from '@/components/humanity/SoftButton';
import HumanityResult from '@/components/humanity/HumanityResult';
import MuteToggle from '@/components/humanity/MuteToggle';
import EntityGallery from '@/components/humanity/EntityGallery';
import LoadingMemoryField from '@/components/humanity/LoadingMemoryField';
import AmbientSound from '@/components/humanity/AmbientSound';
import { useHumanityUiSounds, playUiClick, playEntityActivation, playEntityWake } from '@/components/humanity/humanityUiSounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGENT_ID = 'agent_5501krascfy4fpn9ma0dp9h5x4eq';
const EXHIBITION_SLUG = 'this-is-humanity';

/**
 * Stages:
 *   dormant      — entity is sleeping, awaiting first touch
 *   awakened     — entity revealed, Interact CTA visible
 *   voice_ready  — voice session live (auto-started from Interact)
 *   voice_ended  — entity goodbye, transitioning to generating
 *   loading      — backend generating image (polling)
 *   result       — image shown
 *   final        — closing screen
 */
const LOADING_LINES_AWAITING = [
  'Waiting for the conversation to settle.',
  'Reading the trace.',
];
const LOADING_LINES_PROCESSING = [
  'Forming the image.',
  'Rendering your fragment of humanity.',
];

export default function WhatIsHumanity() {
  // Preload click + activation MP3s once on mount.
  useHumanityUiSounds();
  const location = useLocation();
  const navigate = useNavigate();
  const startsOnGallery = location.pathname.endsWith('/gallery');
  const [stage, setStage] = useState(startsOnGallery ? 'gallery' : 'dormant');
  const [previousStage, setPreviousStage] = useState('dormant');
  const [voiceState, setVoiceState] = useState('sleeping');
  const [sessionId, setSessionId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [generation, setGeneration] = useState(null);
  const [pollError, setPollError] = useState('');
  const [pollDebug, setPollDebug] = useState(null);
  const [loadingLineIdx, setLoadingLineIdx] = useState(0);
  const [pipelineStatus, setPipelineStatus] = useState('awaiting_transcript');
  const [memoryContext, setMemoryContext] = useState({
    entity_essence: 'The entity has only its first beliefs.',
    entity_memory_context: 'No accessible memory yet.',
    memory_count: 0,
  });
  const pollRef = useRef(null);
  const sessionStartedRef = useRef(false);

  // ─── fetch collective memory once on mount ───────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/humanity/entity/memory-context`,
          { params: { exhibition_slug: EXHIBITION_SLUG }, timeout: 8000 },
        );
        if (cancelled || !r?.data) return;
        setMemoryContext({
          entity_essence: r.data.entity_essence || 'The entity has only its first beliefs.',
          entity_memory_context: r.data.entity_memory_context || 'No accessible memory yet.',
          memory_count: r.data.memory_count || 0,
        });
      } catch (err) {
        console.warn('[HumanityMirror] memory-context fetch failed (non-fatal)', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── awaken on first hover/touch ─────────────────────────────
  useEffect(() => {
    if (stage !== 'dormant') return undefined;
    const wake = () => {
      // Wake is a one-shot per dormancy cycle; the listeners themselves are
      // registered with `once: true` and unregistered below.
      playEntityWake();
      setStage('awakened');
    };
    window.addEventListener('pointermove', wake, { once: true, passive: true });
    window.addEventListener('touchstart', wake, { once: true, passive: true });
    return () => {
      window.removeEventListener('pointermove', wake);
      window.removeEventListener('touchstart', wake);
    };
  }, [stage]);

  // ─── start backend session before voice ──────────────────────
  const ensureSession = async () => {
    if (sessionStartedRef.current && sessionId) return sessionId;
    try {
      const res = await axios.post(`${API}/humanity/session/start`, {
        exhibition_slug: EXHIBITION_SLUG,
      });
      const id = res.data?.session_id;
      const token = res.data?.frontend_session_token;
      if (id) {
        setSessionId(id);
        if (token) setSessionToken(token);
        sessionStartedRef.current = true;
        try {
          localStorage.setItem('humanity_session_id', id);
          if (token) localStorage.setItem('humanity_session_token', token);
        } catch { /* ignore */ }
        return id;
      }
    } catch (err) {
      console.error('humanity session/start failed', err);
    }
    return null;
  };

  // Single-click flow: Interact awakens the session and auto-starts voice.
  const handleInteract = async () => {
    // Once-only Jarvis activation sound fires immediately on the gesture
    // so it stays inside the user-gesture window even if the session
    // creation request takes a moment.
    playEntityActivation();
    await ensureSession();
    setStage('voice_ready');
  };

  const handleConversationEnded = async () => {
    setStage('voice_ended');
    setPipelineStatus('awaiting_transcript');
    setTimeout(() => setStage('loading'), 1600);
    try {
      if (sessionId) {
        const r = await axios.post(`${API}/humanity/session/${sessionId}/finish`);
        if (r.data?.status) setPipelineStatus(r.data.status);
      }
    } catch (err) {
      console.warn('finish call non-fatal error', err);
    }
  };

  // ─── poll for the result while in loading state ──────────────
  useEffect(() => {
    if (stage !== 'loading' || !sessionId) return undefined;
    let cancelled = false;
    const startedAt = Date.now();
    // gpt-image-2 + ElevenLabs transcript wait can push end-to-end past
    // 180s on busy days. Give the pipeline ~4 min before surfacing a
    // failure screen.
    const MAX_LOADING_MS = 240000;

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/humanity/session/${sessionId}/result`);
        if (cancelled) return;
        const data = r.data || {};
        if (data.status) setPipelineStatus(data.status);
        if (data.status === 'image_generated' && data.generation?.image_url) {
          setGeneration(data.generation);
          // Hold the loading screen a moment longer so the visitor sees the
          // progress percent animate cleanly to 100% before the artwork
          // result fades in. Otherwise the % jumps mid-flight from ~88 to
          // never-displayed-100, which feels like a hard cut.
          setTimeout(() => {
            if (!cancelled) setStage('result');
          }, 1100);
          return;
        }
        if (data.status === 'failed') {
          setPollError(data.error_message || 'The image did not form.');
          if (data.debug) {
            console.warn('[HumanityMirror] generation failed', data.debug);
            setPollDebug(data.debug);
          }
          return;
        }
        setPollError('');
      } catch (err) {
        console.warn('[HumanityMirror] result poll non-fatal error', err?.message || err);
        setPollError('transient');
      }
      if (Date.now() - startedAt > MAX_LOADING_MS) {
        setPollError('The image did not form.');
        return;
      }
      pollRef.current = setTimeout(tick, 3000);
    };

    tick();

    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [stage, sessionId]);

  // ─── rotate loading copy keyed to current pipeline status ────
  useEffect(() => {
    if (stage !== 'loading') return undefined;
    setLoadingLineIdx(0);
    const i = setInterval(() => setLoadingLineIdx((n) => n + 1), 3200);
    return () => clearInterval(i);
  }, [stage, pipelineStatus]);

  // ─── derive voice state for canvas when not in voice flow ────
  useEffect(() => {
    if (stage === 'dormant') setVoiceState('sleeping');
    else if (stage === 'awakened') setVoiceState('waking');
    else if (stage === 'voice_ended') setVoiceState('ended');
    else if (stage === 'loading') setVoiceState('generating');
    else if (stage === 'result') setVoiceState('sleeping');
    else if (stage === 'gallery') setVoiceState('sleeping');
  }, [stage]);

  // Open/close the entity gallery while preserving the stage we came from
  // so the visitor can return to their result, awakened entity, or dormant.
  const openGallery = () => {
    if (stage !== 'gallery') setPreviousStage(stage);
    setStage('gallery');
    if (!location.pathname.endsWith('/gallery')) navigate('/whatishumanity/gallery');
  };
  const closeGallery = () => {
    const target = previousStage && previousStage !== 'gallery' ? previousStage : 'dormant';
    setStage(target);
    if (location.pathname.endsWith('/gallery')) navigate('/whatishumanity');
  };

  // Sync stage when the route changes (e.g. user navigates back via browser).
  useEffect(() => {
    const onGalleryRoute = location.pathname.endsWith('/gallery');
    if (onGalleryRoute && stage !== 'gallery') {
      setPreviousStage(stage);
      setStage('gallery');
    } else if (!onGalleryRoute && stage === 'gallery') {
      const target = previousStage && previousStage !== 'gallery' ? previousStage : 'dormant';
      setStage(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Split-layout stages: copy at top + entity middle + (optional) bottom slot.
  // Loading uses the same layout so the "RENDERING …" copy + % sits in
  // exactly the same upper-centre position as "INTERACT WITH THE ENTITY".
  const isSplitLayout = stage === 'awakened' || stage === 'voice_ready'
    || stage === 'voice_ended' || stage === 'loading';

  return (
    <ConversationProvider>
      <div className="humanity-root relative min-h-screen w-full overflow-hidden bg-black text-white" data-testid="whatishumanity-page">
        <AmbientCanvas stage={stage} voiceState={voiceState} mode={stage === 'gallery' ? 'gallery' : 'entity'} />

        {/* Ambient soundtrack — looping background music only. Mounted at
            root so it survives stage swaps (dormant ↔ voice ↔ loading ↔
            result ↔ gallery) without restart. Ducks under voiceState; mute
            persists in localStorage. */}
        <AmbientSound voiceState={voiceState} />

        {/* Memory thumbnail field — only mounted while the image is forming.
            Ambient layer behind the entity so the waiting feels like the
            entity searching through its own memory. */}
        <AnimatePresence>
          {stage === 'loading' && (
            <LoadingMemoryField key="loading-memory-field" />
          )}
        </AnimatePresence>

        {/* Gallery overlay — renders above everything when stage='gallery' */}
        <AnimatePresence>
          {stage === 'gallery' && (
            <EntityGallery key="entity-gallery" onReturn={closeGallery} />
          )}
        </AnimatePresence>

        {/* Top exhibition label */}
        <header className="absolute top-5 sm:top-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <p className="font-mono tracking-[0.38em] uppercase text-[9px] sm:text-[10px] text-white/45 text-center whitespace-nowrap">
            THE RELATIVE GALLERY <span className="text-white/25 mx-1.5">//</span> THIS IS HUMANITY
          </p>
        </header>

        {/* Mute toggle in top-right — only renders when connected */}
        <MuteToggle />

        {isSplitLayout ? (
          <main className="relative z-10 min-h-screen flex flex-col px-5 pt-20 pb-10" data-testid="humanity-split-layout">
            {/* Upper slot — same vertical position as "INTERACT WITH THE ENTITY" */}
            <div className="flex-1 flex items-end justify-center pb-4">
              <AnimatePresence mode="wait">
                {stage === 'awakened' && <GreetingPanel key="greet-a" />}
                {stage === 'voice_ended' && <GoodbyePanel key="g" />}
                {stage === 'loading' && (
                  <LoadingTopPanel
                    key="lt"
                    pipelineStatus={pipelineStatus}
                    lineIdx={loadingLineIdx}
                    error={pollError}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Entity zone — canvas renders behind, this is empty spacer */}
            <div className="h-[260px] sm:h-[300px]" aria-hidden />

            {/* Lower slot — interact target / voice controls / loading error */}
            <div className="flex-1 flex items-start justify-center pt-8">
              <AnimatePresence mode="wait">
                {stage === 'awakened' && <InteractCTA key="i" onInteract={handleInteract} />}
                {stage === 'voice_ready' && (
                  <VoicePanel
                    key="v"
                    sessionId={sessionId}
                    sessionToken={sessionToken}
                    memoryContext={memoryContext}
                    onVoiceStateChange={setVoiceState}
                    onConversationEnded={handleConversationEnded}
                    onConversationIdAttached={(cid) => setConversationId(cid)}
                  />
                )}
                {stage === 'loading' && (
                  <LoadingErrorPanel
                    key="le"
                    error={pollError}
                    debug={pollDebug}
                    onRetry={async () => {
                      // Try once again — reuse the same conversation;
                      // backend's _process_job will auto-soften the prompt
                      // if the previous failure was moderation-blocked.
                      if (!sessionId) return;
                      setPollError('');
                      setPollDebug(null);
                      setPipelineStatus('awaiting_transcript');
                      try {
                        await axios.post(`${API}/humanity/session/${sessionId}/rerun`, {}, { timeout: 60000 });
                      } catch (err) {
                        console.warn('[HumanityMirror] rerun failed', err?.response?.data || err?.message);
                      }
                      setStage('loading');
                    }}
                    onCreateNew={() => {
                      // Create a new one — drop everything tied to the
                      // current session so the visitor never sees the old
                      // image / old failure state.
                      setSessionId(null);
                      setSessionToken(null);
                      setConversationId(null);
                      setGeneration(null);
                      setPollError('');
                      setPollDebug(null);
                      setPipelineStatus('awaiting_transcript');
                      sessionStartedRef.current = false;
                      setStage('dormant');
                    }}
                  />
                )}
              </AnimatePresence>
            </div>
          </main>
        ) : (
          <main className="relative z-10 min-h-screen flex flex-col items-center justify-center px-5 pt-24 pb-32">
            <div className="w-full max-w-md flex flex-col items-center text-center">
              <AnimatePresence mode="wait">
                {stage === 'dormant' && <DormantHint key="d" />}
                {stage === 'result' && (
                  <HumanityResult
                    key="r"
                    generation={generation}
                    sessionId={sessionId}
                    onSeeGallery={openGallery}
                    onCreateAnother={() => {
                      // HARD RESET — never reuse any previous session/conversation/
                      // generation/ticket state. Behaves like Ctrl+Shift+R: clear
                      // every humanity_* key, mint a fresh timestamp, force a full
                      // page reload via a cache-busting URL param so React state,
                      // refs, ElevenLabs SDK, and AmbientCanvas all rebuild from
                      // scratch.
                      try {
                        const KEYS = [
                          'humanity_session_id',
                          'humanity_session_token',
                          'humanity_conversation_id',
                        ];
                        KEYS.forEach((k) => {
                          try { localStorage.removeItem(k); } catch { /* ignore */ }
                          try { sessionStorage.removeItem(k); } catch { /* ignore */ }
                        });
                      } catch { /* ignore */ }
                      const url = `/whatishumanity?restart=${Date.now()}`;
                      // window.location.replace prevents the previous result
                      // screen from sitting in history (no back-button reuse).
                      window.location.replace(url);
                    }}
                  />
                )}
                {stage === 'final' && <FinalPanel key="f" />}
              </AnimatePresence>
            </div>
          </main>
        )}

        {/* Bottom hint */}
        <footer className="absolute bottom-5 left-0 right-0 z-10 pointer-events-none">
          <p className="text-center font-mono tracking-[0.36em] uppercase text-[9px] sm:text-[10px] text-white/25">
            {stage === 'dormant' ? 'touch the presence to begin' : '—'}
          </p>
        </footer>

        <MemoryHealthBadge />
      </div>
    </ConversationProvider>
  );
}

// ─── Dev-only memory health badge ─────────────────────────────

function MemoryHealthBadge() {
  const [open, setOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const isDev = typeof window !== 'undefined'
    && (window.location.hostname.includes('preview') || window.location.hostname === 'localhost');

  useEffect(() => {
    if (!isDev || !open || health) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/humanity/entity/memory-health`,
          { params: { exhibition_slug: EXHIBITION_SLUG }, timeout: 8000 },
        );
        if (!cancelled) setHealth(r.data);
      } catch (err) {
        console.warn('[HumanityMirror] memory-health fetch failed', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isDev, health]);

  if (!isDev) return null;

  return (
    <div className="fixed bottom-3 right-3 z-30 max-w-[460px]" data-testid="memory-health-badge">
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[9px] font-mono tracking-[0.28em] uppercase text-white/30 hover:text-white/65 transition-colors border border-white/10 rounded-full px-3 py-1.5 bg-black/50 backdrop-blur"
          data-testid="memory-health-toggle"
        >
          memory · dev
        </button>
      )}
      {open && (
        <div className="rounded-md border border-white/10 bg-black/85 backdrop-blur p-3 text-[10px] font-mono text-white/65 leading-relaxed max-h-[70vh] overflow-auto">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white/40 tracking-[0.2em] uppercase">memory · dev</span>
            <button type="button" onClick={() => setOpen(false)} className="text-white/40 hover:text-white/80" data-testid="memory-health-close">×</button>
          </div>
          {!health && <p className="text-white/40">Loading…</p>}
          {health && (
            <pre className="whitespace-pre-wrap break-words text-[10px] text-white/60">
{`conv: ${health.conversation_count}   memory: ${health.memory_count}   v${health.entity_state?.version ?? '?'}
migration:
  entity_state_new_cols: ${health.migration_status?.entity_state_new_columns}
  fragments_new_scale:   ${health.migration_status?.fragments_new_importance_scale}

long_essence:
${health.entity_state?.long_essence_preview || '—'}

conversation_essence:
${health.entity_state?.conversation_essence_preview || '—'}

worldview_summary:
${health.entity_state?.worldview_summary_preview || '—'}

recurring_themes: ${(health.entity_state?.recurring_themes || []).slice(0, 10).join(' · ') || '—'}

emotional_drift: ${Object.entries(health.entity_state?.emotional_drift || {}).slice(0, 6).map(([k, v]) => `${k}:${Number(v).toFixed(2)}`).join(' · ') || '—'}

learned_questions: ${(health.entity_state?.learned_questions || []).slice(0, 4).map((q) => `\n  - ${q}`).join('') || '—'}

compression_notes.what_changed:
${health.entity_state?.compression_notes?.what_changed || '—'}

dominant_patterns: ${(health.entity_state?.compression_notes?.dominant_patterns || []).join(' · ') || '—'}`}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-panels ────────────────────────────────────────────────

function DormantHint() {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 1.2 }}
      className="text-center"
      data-testid="stage-dormant"
    >
      <motion.div
        className="w-3 h-3 rounded-full bg-white/30 mx-auto"
        animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
    </motion.div>
  );
}

function GreetingPanel() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9 }}
      className="text-center max-w-md"
      data-testid="stage-awakened"
    >
      <p
        className="font-mono tracking-[0.32em] uppercase text-white/75 text-[11px] sm:text-[12px]"
        data-testid="greeting-interact-label"
      >
        Interact with the entity
      </p>
    </motion.div>
  );
}

function InteractCTA({ onInteract }) {
  // The visible CTA from before is gone — the entity itself is now the
  // interaction surface. We render a large circular hit area whose centre
  // overlays the entity's canvas-rendered glow, plus a single subtle
  // breathing ring that hints at clickability without ever reading as a
  // SaaS button.
  const HIT = 220; // px diameter, ~3x the visible core radius
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7 }}
      // Anchored to the viewport centre so it always lines up with the
      // AmbientCanvas-drawn entity core, regardless of layout flow.
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-10"
      data-testid="interact-cta-wrap"
    >
      <button
        type="button"
        onClick={onInteract}
        aria-label="Interact with the entity"
        data-testid="interact-entity-area"
        data-clickable="true"
        className="humanity-entity-hit pointer-events-auto rounded-full group relative bg-transparent border-0 p-0"
        style={{ width: `${HIT}px`, height: `${HIT}px`, transformOrigin: 'center center' }}
      >
        {/* Subtle breathing ring — sits behind the entity glow at low
            opacity so a first-time visitor sees "this area is clickable"
            without the artwork ever showing a UI button. */}
        <span
          aria-hidden
          className="humanity-entity-hit-ring absolute inset-0 rounded-full"
        />
      </button>
    </motion.div>
  );
}

function VoicePanel({ sessionId, sessionToken, memoryContext, onVoiceStateChange, onConversationEnded, onConversationIdAttached }) {
  const dynamicVariables = {
    humanity_session_id: sessionId || '',
    frontend_session_token: sessionToken || '',
    exhibition_slug: EXHIBITION_SLUG,
    entity_essence: memoryContext?.entity_essence || 'The entity has only its first beliefs.',
    entity_memory_context: memoryContext?.entity_memory_context || 'No accessible memory yet.',
    memory_count: String(memoryContext?.memory_count ?? 0),
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full flex flex-col items-center"
      data-testid="stage-voice-ready"
    >
      <ElevenLabsEntityVoice
        agentId={AGENT_ID}
        sessionId={sessionId}
        dynamicVariables={dynamicVariables}
        autoStart
        onVoiceStateChange={onVoiceStateChange}
        onConversationEnded={onConversationEnded}
        onConversationIdAttached={onConversationIdAttached}
      />
    </motion.div>
  );
}

function GoodbyePanel() {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="space-y-2 text-center"
      data-testid="stage-voice-ended"
    >
      <p className="font-mono tracking-[0.28em] uppercase text-white/80 text-[12px] sm:text-[13px]">The image is clear enough.</p>
      <p className="font-mono tracking-[0.22em] uppercase text-white/55 text-[11px] sm:text-[12px]">I will let it form now.</p>
    </motion.div>
  );
}

function useLoadingProgress(pipelineStatus, isTransient) {
  // Smooth phase-aware progress percent. We never know exact backend progress,
  // so we drive a believable curve from pipelineStatus:
  //   awaiting_transcript → 5 → ~30 (slow climb)
  //   processing          → 30 → ~88 (steady climb)
  //   image_generated     → animate quickly to 100
  // Error/transient: hover in the 85–96 range.
  const [pct, setPct] = useState(3);
  const pctRef = useRef(3);
  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    let lastReport = 0;
    const tick = (now) => {
      const elapsed = (now - t0) / 1000;
      let target;
      if (pipelineStatus === 'awaiting_transcript') {
        // Slow approach to 25%, time-constant ~10s.
        target = Math.min(25, 5 + (1 - Math.exp(-elapsed / 10)) * 30);
      } else if (pipelineStatus === 'image_generated') {
        target = 100;
      } else if (isTransient) {
        target = Math.max(pctRef.current, 90);
      } else {
        // processing — climb to 85 over ~140s so the curve covers gpt-image-2
        // generation times (job ~80–110s) without pinning early. Once at
        // 85, drift up to 96 very slowly so the visitor still sees motion.
        target = Math.min(85, 25 + (1 - Math.exp(-elapsed / 22)) * 70);
        if (pctRef.current >= 84) target = Math.min(96, pctRef.current + 0.008);
      }
      const k = pipelineStatus === 'image_generated' ? 0.08 : 0.025;
      pctRef.current += (target - pctRef.current) * k;
      if (now - lastReport > 80) {
        lastReport = now;
        setPct(pctRef.current);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pipelineStatus, isTransient]);
  return pct;
}

function LoadingTopPanel({ pipelineStatus, lineIdx, error }) {
  const lines = pipelineStatus === 'awaiting_transcript'
    ? LOADING_LINES_AWAITING
    : LOADING_LINES_PROCESSING;
  const line = lines[Math.abs(lineIdx) % lines.length];
  const isTransient = error === 'transient';
  const isFatal = error && !isTransient;
  const pct = useLoadingProgress(pipelineStatus, isTransient);
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7 }}
      className="text-center max-w-md"
      data-testid="stage-loading"
    >
      <AnimatePresence mode="wait">
        <motion.p
          key={isTransient ? 'transient' : line}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.5 }}
          className="font-mono tracking-[0.32em] uppercase text-white/75 text-[11px] sm:text-[12px]"
          data-testid="loading-copy"
        >
          {isTransient ? 'The image is still forming.' : line}
        </motion.p>
      </AnimatePresence>
      {!isFatal && (
        <p
          className="mt-2 font-mono tracking-[0.24em] text-[11px] sm:text-[12px] text-white/55 tabular-nums"
          data-testid="loading-progress"
        >
          {Math.max(0, Math.min(100, Math.round(pct)))}%
        </p>
      )}
    </motion.div>
  );
}

function LoadingErrorPanel({ error, debug, onRetry, onCreateNew }) {
  const isTransient = error === 'transient';
  const isFatal = error && !isTransient;
  const isDev = typeof window !== 'undefined'
    && (window.location.hostname.includes('preview') || window.location.hostname === 'localhost');
  const [traceOpen, setTraceOpen] = useState(false);
  if (!isFatal) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="space-y-4 text-center"
      data-testid="loading-error"
    >
      <p className="font-mono tracking-[0.30em] uppercase text-[11px] sm:text-[12px] text-white/75">
        Image didn&rsquo;t form.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <SoftButton
          variant="cyan"
          size="sm"
          onClick={() => { playUiClick(); onRetry?.(); }}
          testId="retry-btn"
        >
          Try once again
        </SoftButton>
        <SoftButton
          variant="ghost"
          size="sm"
          onClick={() => { playUiClick(); onCreateNew?.(); }}
          testId="create-new-btn"
        >
          Create a new one
        </SoftButton>
      </div>
      {isDev && debug && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setTraceOpen((v) => !v)}
            data-testid="technical-trace-toggle"
            data-clickable="true"
            className="font-mono tracking-[0.28em] uppercase text-[9px] text-white/35 hover:text-white/65 underline underline-offset-[3px] decoration-white/15 hover:decoration-white/40 transition-colors"
          >
            {traceOpen ? 'Hide technical trace' : 'Technical trace'}
          </button>
          {traceOpen && (
            <pre
              className="mt-3 text-[10px] font-mono text-left text-white/40 leading-relaxed max-w-md mx-auto p-3 rounded-md border border-white/10 bg-white/[0.02] whitespace-pre-wrap break-words"
              data-testid="loading-debug"
            >
{`stage: ${debug.failure_stage || 'unknown'}
session_status: ${debug.session_status || '—'}
conv_id_present: ${debug.elevenlabs_conversation_id_present}
message_count: ${debug.message_count}
job_status: ${debug.latest_job_status || '—'}
gen_status: ${debug.latest_generation_status || '—'}
error: ${debug.latest_job_error || debug.latest_generation_error || '—'}`}
            </pre>
          )}
        </div>
      )}
    </motion.div>
  );
}

function FinalPanel() {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="space-y-3 text-center"
      data-testid="stage-final"
    >
      <p className="font-outfit text-lg text-white/80">Thank you for being part of the mirror.</p>
      <p className="font-mono tracking-[0.28em] uppercase text-[10px] text-white/30">
        Your fragment now belongs to the exhibition.
      </p>
    </motion.div>
  );
}
